import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatDateShort } from '../lib/datetime'

/**
 * Adminvy över alla gäng och deras medlemmar.
 *
 * Läser `group_roster`, som är `security_invoker` — samma vy används av
 * deltagare och visar då bara det egna gänget. Sidan är ändå adminskyddad,
 * eftersom den listar e-postadresser.
 *
 * Ett gäng utan medlemmar syns med noll rader i stället för att försvinna:
 * ett tomt gäng är oftast något som ska rättas, och det går inte att rätta
 * det man inte ser. Vyn är därför en left join.
 */
interface RosterRow {
  group_id: string
  group_name: string
  slug: string
  is_default: boolean
  created_at: string
  user_id: string | null
  display_name: string | null
  nickname: string | null
  email: string | null
  role: string | null
  is_manager: boolean | null
  joined_at: string | null
}

interface GroupBlock {
  id: string
  name: string
  slug: string
  isDefault: boolean
  members: RosterRow[]
}

export function AdminGroups() {
  const { profile } = useAuth()
  const [blocks, setBlocks] = useState<GroupBlock[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('group_roster')
      .select('*')
      .order('group_name')

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    const rows = (data as RosterRow[]) ?? []
    const byGroup = new Map<string, GroupBlock>()

    for (const r of rows) {
      let block = byGroup.get(r.group_id)
      if (!block) {
        block = {
          id: r.group_id,
          name: r.group_name,
          slug: r.slug,
          isDefault: r.is_default,
          members: [],
        }
        byGroup.set(r.group_id, block)
      }
      if (r.user_id) block.members.push(r)
    }

    for (const b of byGroup.values()) {
      b.members.sort((a, z) => (a.display_name ?? '').localeCompare(z.display_name ?? '', 'sv'))
    }

    setBlocks([...byGroup.values()])
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  const members = blocks.reduce((n, b) => n + b.members.length, 0)

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1>Gäng och medlemmar</h1>
        <p className="muted">
          {loading
            ? 'Hämtar…'
            : `${blocks.length} gäng, ${members} medlemskap. Flytta någon på `}
          {!loading && <Link to="/admin/anvandare">Deltagare</Link>}
          {!loading && '.'}
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {blocks.map((b) => (
        <article key={b.id} className="card stack">
          <div>
            <h2>
              {b.name}
              {b.isDefault && <span className="tag" style={{ marginLeft: 10 }}>standard</span>}
            </h2>
            <p className="muted">
              /{b.slug} · {b.members.length}{' '}
              {b.members.length === 1 ? 'medlem' : 'medlemmar'}
              {b.isDefault && ' · hit placeras alla som registrerar sig utan inbjudan'}
            </p>
          </div>

          {b.members.length === 0 ? (
            <p className="muted">Inga medlemmar.</p>
          ) : (
            <div className="board">
              <div className="roster__row roster__row--head">
                <span>Namn</span>
                <span>Smeknamn</span>
                <span>E-post</span>
                <span>Med sedan</span>
              </div>
              {b.members.map((m) => (
                <div key={m.user_id} className="roster__row">
                  <span>
                    {m.display_name}
                    {m.is_manager && <span className="muted"> · ansvarig</span>}
                    {m.role === 'admin' && <span className="muted"> · admin</span>}
                    {m.role === 'rattare' && <span className="muted"> · rättare</span>}
                  </span>
                  <span className="muted">{m.nickname ?? '–'}</span>
                  <span className="muted">{m.email}</span>
                  <span className="muted">{formatDateShort(m.joined_at)}</span>
                </div>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  )
}
